import axios from 'axios';
import { withFreshAuth } from './freshAuth';
import { ENDPOINTS } from '../config/endpoints';
import { CONSENT_VERSION } from '../config/legalVersions';

// Клиент bit-billing (тарифы/подписки/заявки на счёт). Пути и формы JSON —
// bit-billing/app/api.py. В dev проксируется src/setupProxy.js, в prod — nginx.conf.template
// (location /billing-api/ — иначе прод молча сломается, см. CLAUDE.md).
const API_BASE_URL = ENDPOINTS.billing;

// Оплата картой (ЮKassa) — фаза 4; до включения кнопка «Оплатить картой» задизейблена.
export const CARD_PAYMENTS_ENABLED = process.env.REACT_APP_BILLING_CARD_ENABLED === 'true';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

api.interceptors.request.use(withFreshAuth);

export const billingService = {
  // Демо-доступ клиенту от менеджера офиса: «Малый» на месяц сразу, одно на ИНН.
  // body: { company_name, inn, client_email, office, comment? } → { id, status, client_email, paid_until }
  async createDemo(body) {
    const response = await api.post('/demo-requests', body);
    return response.data;
  },

  // --- Клиентские ---

  async getPlans() {
    // → { plans: [...] }; ставка перерасхода своя у каждого тарифа, внутри plan
    const response = await api.get('/plans');
    return response.data;
  },

  async getMySubscription() {
    // Активная подписка или синтетический Free (не 404)
    const response = await api.get('/me/subscription');
    return response.data;
  },

  async getMyOrganization() {
    // → { organization: {...} | null }. Нет организации — null, а не 404:
    // её отсутствие такое же обычное состояние, как отсутствие подписки.
    const response = await api.get('/me/organization');
    return response.data;
  },

  async inviteOrgMember(email) {
    const response = await api.post('/me/organization/members', { email });
    return response.data;
  },

  async removeOrgMember(email) {
    const response = await api.delete(`/me/organization/members/${encodeURIComponent(email)}`);
    return response.data;
  },

  async renameOrganization(name) {
    const response = await api.patch('/me/organization', { name });
    return response.data;
  },

  async getMyPayments(params = {}) {
    // params: { page, page_size } → { items, total_count }
    const response = await api.get('/me/payments', { params });
    return response.data;
  },

  async getBalanceOperations(params = {}) {
    // Журнал операций по балансу: пополнения и списания за закрытые месяцы.
    // params: { page, page_size } → { items, total_count }
    const response = await api.get('/me/balance-operations', { params });
    return response.data;
  },

  async createPayment(body) {
    // body: { plan_code, billing_period, auto_renew?, offer_version? }
    //   → { order_ref, amount_rub, confirmation_url }
    const response = await api.post('/payments', body);
    return response.data;
  },

  async disableAutoRenew() {
    // «Отключить» автопродление (оферта п. 6.9) → { auto_renew: false }
    const response = await api.delete('/me/subscription/auto-renew');
    return response.data;
  },

  async deleteCard() {
    // «Удалить карту»: отвязка в платёжном шлюзе, автопродление выключается вместе с ней
    const response = await api.delete('/me/subscription/card');
    return response.data;
  },

  async createInvoiceRequest(body) {
    // body: { plan_code, billing_period, company_name, inn, kpp?, contact_phone?, comment? }
    const response = await api.post('/invoice-requests', body);
    return response.data;
  },

  async getMyInvoiceRequests(params = {}) {
    const response = await api.get('/invoice-requests', { params });
    return response.data;
  },

  // --- Админские (вкладка «Биллинг» в админке) ---

  async adminGetInvoiceRequests(params = {}) {
    // params: { status, email, page, page_size }
    const response = await api.get('/admin/invoice-requests', { params });
    return response.data;
  },

  async adminUpdateInvoiceRequest(id, fields) {
    // fields: { status?: invoiced|paid|rejected, admin_note? }
    const response = await api.patch(`/admin/invoice-requests/${encodeURIComponent(id)}`, fields);
    return response.data;
  },

  async adminActivate(id, body = {}) {
    // Активация тарифа по оплаченной заявке; body: { paid_from? }
    const response = await api.post(`/admin/invoice-requests/${encodeURIComponent(id)}/activate`, body);
    return response.data;
  },

  async adminGetPayments(params = {}) {
    // params: { email, method, page, page_size }; method=bank_transfer — оплаты по счёту
    const response = await api.get('/admin/payments', { params });
    return response.data;
  },

  // Оплата по счёту: запись по выписке банка, тариф не выдаёт
  // (спека docs/superpowers/specs/2026-09-28-office-share-and-invoice-payments-design.md).
  async adminRecordOfflinePayment(body) {
    const response = await api.post('/admin/offline-payments', body);
    return response.data;
  },

  async adminPushOfflinePayment(orderRef) {
    const response = await api.post(`/admin/offline-payments/${encodeURIComponent(orderRef)}/push`);
    return response.data;
  },

  async adminReverseOfflinePayment(orderRef, reason) {
    const response = await api.post(
      `/admin/offline-payments/${encodeURIComponent(orderRef)}/reversal`, { reason });
    return response.data;
  },

  async adminGetSubscriptions(params = {}) {
    // params: { email, page, page_size }
    const response = await api.get('/admin/subscriptions', { params });
    return response.data;
  },

  async adminCreateSubscription(body) {
    // body: { email, plan_code, billing_period, paid_from? } — ручная выдача тарифа
    const response = await api.post('/admin/subscriptions', body);
    return response.data;
  },

  async adminUpdateSubscription(id, fields) {
    // fields: { status?: canceled, paid_until? }
    const response = await api.patch(`/admin/subscriptions/${encodeURIComponent(id)}`, fields);
    return response.data;
  },

  // --- Лид-магнит «месяц Малого за реквизиты» ---

  // Заявку шлём ГОЛЫМ axios, без общего инстанса: форма живёт на публичной
  // странице, которая рендерится до initKeycloak, и перехватчик с
  // Authorization ей не нужен. Ручка на бэкенде тоже открыта.
  async requestTrial(body) {
    // body: { company_name, inn, contact_name, contact_email, contact_phone?, comment? }
    try {
      const response = await axios.post(
        `${API_BASE_URL}/trial-requests`,
        { ...body, consent_version: CONSENT_VERSION },
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      if (status === 400) throw new Error('Проверьте ИНН: 10 цифр у организации, 12 у ИП.');
      if (status === 429) throw new Error('Слишком много заявок. Попробуйте завтра или напишите нам письмом.');
      throw new Error('Не удалось отправить заявку. Попробуйте позже.');
    }
  },

  // Организация поверх УЖЕ купленной подписки: для тех, кто заплатил до
  // появления организаций. Через adminCreateSubscription это делать нельзя —
  // для активной подписки там продление, и клиент получает месяц в подарок.
  async adminGetOrganizations(params = {}) {
    // params: { search, page, page_size } → { items, total_count }.
    // items несут id, название, плательщика, тариф, состояние и состав.
    const response = await api.get('/admin/organizations', { params });
    return response.data;
  },

  async adminCreateOrganization(body) {
    const response = await api.post('/admin/organizations', body);
    return response.data;
  },

  async adminAddOrgMember(orgId, email) {
    const response = await api.post(`/admin/organizations/${orgId}/members`, { email });
    return response.data;
  },

  // Состав организации клиента с исключёнными (Р11) → { items: [{email, role, status, ...}] }.
  async adminGetOrgMembers(orgId) {
    const response = await api.get(`/admin/organizations/${orgId}/members`);
    return response.data;
  },

  async adminRemoveOrgMember(orgId, email) {
    const response = await api.delete(`/admin/organizations/${orgId}/members/${encodeURIComponent(email)}`);
    return response.data;
  },

  // Кто в какой организации сейчас (Р12) → { items: [{email, org_id, org_name, role}] }.
  async adminGetOrgMemberships() {
    const response = await api.get('/admin/org-memberships');
    return response.data;
  },

  async adminChangeOrgOwner(orgId, ownerEmail) {
    const response = await api.patch(`/admin/organizations/${orgId}`, { owner_email: ownerEmail });
    return response.data;
  },

  async adminGetTrialRequests(params = {}) {
    // params: { status, page, page_size }
    const response = await api.get('/admin/trial-requests', { params });
    return response.data;
  },

  async adminActivateTrial(id) {
    const response = await api.post(`/admin/trial-requests/${encodeURIComponent(id)}/activate`);
    return response.data;
  },

  // Отказ по заявке: заявка уходит из очереди, тариф не выдаётся.
  async adminRejectTrial(id, adminNote) {
    const response = await api.patch(`/admin/trial-requests/${encodeURIComponent(id)}`, {
      status: 'rejected',
      ...(adminNote ? { admin_note: adminNote } : {}),
    });
    return response.data;
  },

  // Пополнение баланса «Оплаты по факту». Ответ тот же, что у оплаты тарифа:
  // клиента уводим на confirmation_url платёжной формы.
  async topUp(amountRub) {
    try {
      const response = await api.post('/payments', {
        purpose: 'topup',
        amount_rub: amountRub,
      });
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      if (status === 400) throw new Error(error.response?.data?.detail || 'Проверьте сумму.');
      if (status === 503) throw new Error('Оплата картой временно недоступна.');
      // 403 — платит только клиент, 409 — платит плательщик организации: текст сервера точнее.
      if ((status === 403 || status === 409) && error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw new Error('Не удалось создать платёж. Попробуйте позже.');
    }
  },
};
